import type { CreateSubscriptionInput, InvoiceStatus, SubFrequency, UpdateSubscriptionInput } from '@financiar/shared';
import { SUBSCRIPTION_NOTE_VARIABLES } from '@financiar/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Card, Col, DatePicker, Drawer, Form, Input, InputNumber, Radio, Row, Select, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { Button } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { getApiError } from '../../api/client';
import type { SubscriptionGeneratedInvoiceRef } from '../../api/types';
import { LineItemsEditor, emptyLine, toLinePayload, type VatCategoryOption } from '../../components/LineItemsEditor';
import { applyServerErrors } from '../../lib/forms';
import { DateText, formatMoney, formatNumber } from '../../lib/format';
import { fetchClienti } from '../clienti/api';
import { fetchFactura } from '../facturi/api';
import { computeLiveTotals, makeVatResolver, VAT_CATEGORY_LABELS, VAT_CATEGORY_ORDER, type LineFormValue } from '../facturi/totals';
import { TotalsPanel } from '../facturi/TotalsPanel';
import { fetchFirma } from '../firme/api';
import { fetchProduse, fetchVatRates } from '../produse/api';
import { fetchSeries } from '../serii/api';
import { createAbonament, fetchAbonament, updateAbonament } from './api';

const FREQUENCY_OPTIONS: { value: SubFrequency; label: string }[] = [
  { value: 'MONTHLY', label: 'Lunar' },
  { value: 'QUARTERLY', label: 'Trimestrial' },
  { value: 'ANNUAL', label: 'Anual' },
];

/** Same pragmatic cap as `FacturaEditorPage`'s `PRODUCTS_PAGE_SIZE`. */
const PRODUCTS_PAGE_SIZE = 100;

interface AbonamentFormValues {
  name: string;
  clientId?: string;
  seriesId?: string;
  frequency?: SubFrequency;
  issueDay?: number;
  startDate?: Dayjs;
  endDate?: Dayjs | null;
  /** The Radio's value: `false` = "Ciornă (recomandat)", `true` = "Emisă
   * automat" — maps 1:1 onto the API's `autoIssue`. */
  autoIssue: boolean;
  paymentTermDays?: number;
  notes?: string;
  items: LineFormValue[];
}

/** A `@db.Date` column arrives as a full ISO timestamp; slicing the date
 * part is exact (same helper as `FacturaEditorPage`'s). */
function dayjsFromIsoDate(value: string | null | undefined): Dayjs | undefined {
  return value ? dayjs(value.slice(0, 10)) : undefined;
}

/** The server projects at most the last 10 generated invoices onto
 * `SubscriptionDetail.generatedInvoices` (SubscriptionsService.findOne) —
 * kept in sync here so the "showing the last N" hint can't drift from it. */
const GENERATED_INVOICES_CAP = 10;

/** A compact status badge for a generated invoice — only the three
 * `InvoiceStatus` values a subscription can produce (no payment data is
 * projected onto `SubscriptionGeneratedInvoiceRef`, unlike the invoices
 * list's fuller `StatusTag`). */
function invoiceStatusTag(status: InvoiceStatus) {
  if (status === 'DRAFT') return <Tag>Ciornă</Tag>;
  if (status === 'CANCELLED') return <Tag>Anulată</Tag>;
  return <Tag color="blue">Emisă</Tag>;
}

const generatedInvoiceColumns: ColumnsType<SubscriptionGeneratedInvoiceRef> = [
  {
    title: 'Număr',
    key: 'number',
    // A DRAFT has no `fullNumber` yet — link its "Ciornă" placeholder to the
    // same invoice view so every row is openable.
    render: (_, row) => <Link to={`/facturi/${row.id}`}>{row.fullNumber ?? 'Ciornă'}</Link>,
  },
  { title: 'Perioadă', key: 'period', width: 130, render: (_, row) => <DateText value={row.periodStart} /> },
  { title: 'Status', key: 'status', width: 110, render: (_, row) => invoiceStatusTag(row.status) },
  {
    title: 'Valoare',
    key: 'amount',
    width: 150,
    align: 'right',
    render: (_, row) => formatMoney(row.totalAmount),
  },
];

/** The "Istoric facturi generate" section at the foot of the edit Drawer —
 * the invoices this subscription has produced (cron sweep or "Generează
 * acum"), newest first. Read-only; refreshes with the detail query. */
function GeneratedInvoicesSection({ invoices }: { invoices: SubscriptionGeneratedInvoiceRef[] }) {
  return (
    <Card title="Istoric facturi generate" size="small" style={{ marginTop: 16 }}>
      {invoices.length === 0 ? (
        <Typography.Text type="secondary">Nicio factură generată încă.</Typography.Text>
      ) : (
        <>
          <Table<SubscriptionGeneratedInvoiceRef>
            rowKey="id"
            size="small"
            columns={generatedInvoiceColumns}
            dataSource={invoices}
            pagination={false}
          />
          {invoices.length >= GENERATED_INVOICES_CAP ? (
            <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
              Se afișează ultimele {GENERATED_INVOICES_CAP} facturi. Pentru istoricul complet (inclusiv eșecurile de
              generare), vezi tab-ul „Istoric generări".
            </Typography.Text>
          ) : null}
        </>
      )}
    </Card>
  );
}

/**
 * `/abonamente`'s create/edit Drawer (task-8d-brief.md §2). Structurally the
 * same shape as `FacturaEditorPage` — firm-scoped reference data, the SHARED
 * `LineItemsEditor` for the item table, live totals from the same
 * `computeInvoiceTotals` math — but as a single-save Drawer rather than a
 * draft/issue two-step page, since a subscription has no "issued" state of
 * its own to freeze into.
 */
export function AbonamentFormDrawer({
  open,
  firmaId,
  editingId,
  prefillInvoiceId,
  onClose,
}: {
  open: boolean;
  firmaId: string;
  editingId: string | null;
  /** When creating (editingId null), pre-fill the form from this invoice's
   * client + lines — "create a subscription from this invoice". */
  prefillInvoiceId?: string | null;
  onClose: () => void;
}) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<AbonamentFormValues>();
  const isEdit = editingId !== null;
  const [clientSearch, setClientSearch] = useState('');

  const { data: sub, isLoading: isLoadingSub } = useQuery({
    queryKey: ['subscriptions', 'detail', editingId],
    queryFn: () => fetchAbonament(editingId as string),
    enabled: open && isEdit,
  });

  const { data: prefillInvoice } = useQuery({
    queryKey: ['invoices', 'detail', prefillInvoiceId],
    queryFn: () => fetchFactura(prefillInvoiceId as string),
    enabled: open && !isEdit && !!prefillInvoiceId,
  });

  const { data: firmDetail } = useQuery({
    queryKey: ['firms', 'detail', firmaId],
    queryFn: () => fetchFirma(firmaId),
    enabled: open && !!firmaId,
  });
  // Same "read from the firm DETAIL response" reasoning as FacturaEditorPage
  // — never from the FirmaCurenta list (see that page's comment).
  const isVatPayerToday = firmDetail?.isVatPayerToday ?? false;

  const { data: series } = useQuery({
    queryKey: ['series', firmaId],
    queryFn: () => fetchSeries(firmaId),
    enabled: open && !!firmaId,
  });
  const seriesOptions = useMemo(
    () => (series ?? []).filter((s) => s.docType === 'INVOICE' && s.isActive).map((s) => ({ value: s.id, label: s.prefix })),
    [series],
  );

  const { data: productsPage, isLoading: isLoadingProducts } = useQuery({
    queryKey: ['products', firmaId, { page: 1, pageSize: PRODUCTS_PAGE_SIZE }],
    queryFn: () => fetchProduse({ companyId: firmaId, page: 1, pageSize: PRODUCTS_PAGE_SIZE }),
    enabled: open && !!firmaId,
  });

  const { data: clientsPage } = useQuery({
    queryKey: ['clients', firmaId, { page: 1, pageSize: 20, search: clientSearch }],
    queryFn: () => fetchClienti({ companyId: firmaId, page: 1, pageSize: 20, search: clientSearch || undefined }),
    enabled: open && !!firmaId,
  });

  const { data: vatRates } = useQuery({
    queryKey: ['vat-rates'],
    queryFn: fetchVatRates,
    staleTime: 60 * 60 * 1000,
    enabled: open,
  });

  const vatCategoryOptions: VatCategoryOption[] = useMemo(() => {
    const rateByCategory = new Map((vatRates ?? []).map((rate) => [rate.category, rate.ratePercent]));
    return VAT_CATEGORY_ORDER.map((category) => {
      const rate = rateByCategory.get(category);
      return rate
        ? { value: category, label: `${VAT_CATEGORY_LABELS[category]} (${formatNumber(rate)}%)` }
        : { value: category, label: `${VAT_CATEGORY_LABELS[category]} (fără cotă valabilă)`, disabled: true };
    });
  }, [vatRates]);

  const watchedItems = Form.useWatch('items', form) as LineFormValue[] | undefined;
  const liveTotals = useMemo(
    () => computeLiveTotals(watchedItems, makeVatResolver(isVatPayerToday, vatRates ?? [])),
    [watchedItems, isVatPayerToday, vatRates],
  );
  const computedLines = liveTotals.status === 'ok' ? liveTotals.totals.lines : null;

  const clientOptions = useMemo(() => {
    const options = new Map<string, string>();
    for (const client of clientsPage?.items ?? []) options.set(client.id, client.name);
    if (sub?.client && !options.has(sub.client.id)) options.set(sub.client.id, sub.client.name);
    return [...options].map(([value, label]) => ({ value, label }));
  }, [clientsPage, sub]);

  useEffect(() => {
    if (!open) return;
    if (isEdit && sub) {
      form.setFieldsValue({
        name: sub.name,
        clientId: sub.clientId,
        seriesId: sub.seriesId,
        frequency: sub.frequency,
        issueDay: sub.issueDay,
        startDate: dayjsFromIsoDate(sub.startDate),
        endDate: sub.endDate ? dayjsFromIsoDate(sub.endDate) : null,
        autoIssue: sub.autoIssue,
        paymentTermDays: sub.paymentTermDays ?? undefined,
        notes: sub.notes ?? undefined,
        items: sub.items.map((item) => ({
          productId: item.productId ?? undefined,
          description: item.description,
          quantity: item.quantity,
          unitCode: item.unitCode,
          unitPrice: item.unitPrice,
          vatCategory: item.vatCategory,
        })),
      });
    } else if (!isEdit) {
      form.resetFields();
      const defaultSeries = (series ?? []).find((s) => s.docType === 'INVOICE' && s.isActive && s.isDefault);
      if (prefillInvoice) {
        // Seed a new subscription from an existing invoice: its client and
        // lines carry over; the recurrence starts today, issues on the 15th and
        // auto-emits by default (the usual "turn this invoice into a monthly").
        form.setFieldsValue({
          name: `Abonament ${prefillInvoice.client.name}`,
          clientId: prefillInvoice.clientId,
          seriesId: prefillInvoice.seriesId ?? defaultSeries?.id,
          frequency: 'MONTHLY',
          startDate: dayjs(),
          issueDay: 15,
          autoIssue: true,
          items: prefillInvoice.lines.map((line) => ({
            productId: line.productId ?? undefined,
            description: line.description,
            quantity: line.quantity,
            unitCode: line.unitCode,
            unitPrice: line.unitPrice,
            vatCategory: line.vatCategory,
          })),
        });
      } else {
        form.setFieldsValue({
          frequency: 'MONTHLY',
          startDate: dayjs(),
          issueDay: 15,
          autoIssue: true,
          seriesId: defaultSeries?.id,
          items: [emptyLine()],
        });
      }
    }
  }, [open, isEdit, sub, series, form, prefillInvoice]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['subscriptions', firmaId] });
    if (editingId) queryClient.invalidateQueries({ queryKey: ['subscriptions', 'detail', editingId] });
  };

  const createMutation = useMutation({
    mutationFn: (values: CreateSubscriptionInput) => createAbonament(values),
    onSuccess: () => {
      message.success('Abonamentul a fost creat.');
      invalidate();
      onClose();
    },
    onError: (error) => {
      const apiError = getApiError(error);
      if (!applyServerErrors(form, apiError)) message.error(apiError?.message ?? 'Abonamentul nu a putut fi creat.');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (values: UpdateSubscriptionInput) => updateAbonament(editingId as string, values),
    onSuccess: () => {
      message.success('Abonamentul a fost actualizat.');
      invalidate();
      onClose();
    },
    onError: (error) => {
      const apiError = getApiError(error);
      if (!applyServerErrors(form, apiError)) message.error(apiError?.message ?? 'Abonamentul nu a putut fi actualizat.');
    },
  });

  function handleFinish(values: AbonamentFormValues) {
    const common = {
      clientId: values.clientId as string,
      seriesId: values.seriesId as string,
      name: values.name.trim(),
      frequency: values.frequency as SubFrequency,
      startDate: (values.startDate as Dayjs).format('YYYY-MM-DD'),
      endDate: values.endDate ? values.endDate.format('YYYY-MM-DD') : undefined,
      issueDay: values.issueDay as number,
      autoIssue: values.autoIssue,
      paymentTermDays: values.paymentTermDays,
      // Sent even when empty, exactly like FacturaEditorPage's `notes` —
      // `undefined` would be dropped from the JSON body and a PATCH could
      // never clear an existing note.
      notes: (values.notes ?? '').trim(),
      items: (values.items ?? []).map(toLinePayload),
    };
    if (isEdit) {
      updateMutation.mutate(common as UpdateSubscriptionInput);
    } else {
      createMutation.mutate({ companyId: firmaId, currency: 'RON', ...common } as CreateSubscriptionInput);
    }
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;
  // An ENDED subscription can't be PATCHed (SubscriptionsService.update()
  // throws SUBSCRIPTION_NOT_ACTIVE) — the drawer still opens read-only so a
  // user can see what it was, but nothing here can be submitted.
  const isEnded = isEdit && sub?.status === 'ENDED';

  /** Append a note-template variable to the current note text — a space is
   * inserted between it and any preceding non-blank text so tokens never jam
   * together. */
  function insertNoteVariable(token: string) {
    const current = (form.getFieldValue('notes') as string | undefined) ?? '';
    const separator = current.length > 0 && !current.endsWith(' ') ? ' ' : '';
    form.setFieldValue('notes', `${current}${separator}${token}`);
  }

  return (
    <Drawer
      title={isEdit ? 'Editează abonamentul' : 'Abonament nou'}
      width="clamp(760px, 52vw, 1200px)"
      open={open}
      onClose={onClose}
      destroyOnHidden
      extra={
        <Button type="primary" onClick={() => form.submit()} loading={isSaving} disabled={isEnded}>
          Salvează
        </Button>
      }
    >
      {isEnded ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Abonamentul s-a încheiat și nu mai poate fi editat."
        />
      ) : null}

      <Form<AbonamentFormValues>
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        disabled={(isEdit && isLoadingSub) || isEnded}
      >
        <Form.Item name="name" label="Denumire abonament" rules={[{ required: true, message: 'Denumirea este obligatorie.' }]}>
          <Input placeholder="ex. Mentenanță lunară" />
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item name="clientId" label="Client" rules={[{ required: true, message: 'Clientul este obligatoriu.' }]}>
              <Select
                showSearch
                filterOption={false}
                placeholder="Caută clientul"
                options={clientOptions}
                onSearch={setClientSearch}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="seriesId" label="Serie" rules={[{ required: true, message: 'Seria este obligatorie.' }]}>
              <Select options={seriesOptions} placeholder="Alege seria" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Form.Item name="frequency" label="Frecvență" rules={[{ required: true, message: 'Frecvența este obligatorie.' }]}>
              <Select options={FREQUENCY_OPTIONS} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="issueDay"
              label="Ziua emiterii"
              extra="31 = ultima zi a lunii (clampată automat pentru lunile mai scurte)."
              rules={[{ required: true, message: 'Ziua emiterii este obligatorie.' }]}
            >
              <InputNumber min={1} max={31} precision={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="paymentTermDays" label="Termen de plată (zile)">
              <InputNumber min={0} precision={0} style={{ width: '100%' }} placeholder="Implicit din client/firmă" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item name="startDate" label="Dată start" rules={[{ required: true, message: 'Data de start este obligatorie.' }]}>
              <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="endDate" label="Dată sfârșit" extra="Gol = fără dată de încheiere">
              <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} allowClear />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="autoIssue" label="Mod generare">
          <Radio.Group>
            <Radio value={false}>Ciornă (recomandat)</Radio>
            <Radio value={true}>Emisă automat</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          name="notes"
          label="Notă pe factură (șablon)"
          extra={
            <div style={{ marginTop: 6 }}>
              <div style={{ marginBottom: 6 }}>
                Textul apare ca notă pe fiecare factură generată. Lasă gol pentru a nu pune nicio notă. Apasă o
                variabilă pentru a o insera — se înlocuiește la generare cu data corespunzătoare (format DD.MM.YYYY):
              </div>
              <Space size={[4, 4]} wrap>
                {SUBSCRIPTION_NOTE_VARIABLES.map((variable) => (
                  <Tooltip key={variable.token} title={variable.description}>
                    <Tag
                      onClick={isEnded ? undefined : () => insertNoteVariable(variable.token)}
                      style={{ cursor: isEnded ? 'default' : 'pointer', margin: 0, fontFamily: 'monospace' }}
                    >
                      {variable.token}
                    </Tag>
                  </Tooltip>
                ))}
              </Space>
            </div>
          }
        >
          <Input.TextArea
            rows={2}
            placeholder="ex. Servicii pentru perioada [prima zi a lunii anterioare] - [ultima zi a lunii anterioare]"
          />
        </Form.Item>

        <Card title="Linii" size="small" style={{ marginBottom: 16 }}>
          <LineItemsEditor
            form={form}
            listName="items"
            products={productsPage?.items ?? []}
            isLoadingProducts={isLoadingProducts}
            vatCategoryOptions={vatCategoryOptions}
            showVat={isVatPayerToday}
            computedLines={computedLines}
          />
        </Card>

        <Row gutter={16} justify="end">
          <Col xs={24} md={12}>
            <TotalsPanel totals={liveTotals} showVat={isVatPayerToday} />
          </Col>
        </Row>
      </Form>

      {isEdit && sub ? <GeneratedInvoicesSection invoices={sub.generatedInvoices} /> : null}
    </Drawer>
  );
}
